/**
 * Parse DF AI summary text into blocks for web + PDF rendering.
 * Tables: consecutive markdown-style pipe rows.
 * Headings: markdown # lines, or known major section titles on their own line.
 * Bullets: -, *, •
 * Sublabels: **bold** line, or short "Label:" lines (e.g. Coverage:)
 */

const SEPARATOR_CELL = /^:?-{3,}:?$/;
const BULLET_RE = /^[-*•]\s+(.+)$/;
const SUB_BOLD_RE = /^\*\*(.+?)\*\*\s*$/;
/** Short label line ending with colon (Coverage:, Market Movements:) */
const SUB_LABEL_RE = /^[A-Z][^:\n]{0,70}:\s*$/;
const FOOTER_RE = /^Report by:/i;
const MAJOR_SECTION_RE =
  /^(Executive Pricing Brief|Portfolio Overview|Price Position Summary|Executive Narrative|Category Intelligence|Sub Category Spotlight|SKU-Level Highlights|AI Closing Note)\b/i;

/** Caption line placed immediately before a markdown table: [Table] … or TABLE_BRIEF: … */
const TABLE_CAPTION_RE = /^\[Table\]\s*(.+)$/i;
const TABLE_BRIEF_RE = /^TABLE_BRIEF:\s*(.+)$/i;

export function stripHeadingPrefix(text) {
  if (!text || typeof text !== "string") return { isHeading: false, text: text || "" };
  const trimmed = text.trim();
  const m = trimmed.match(/^#{1,6}\s*(.+)$/);
  if (m && m[1].trim()) return { isHeading: true, text: m[1].trim() };
  return { isHeading: false, text: trimmed };
}

function isMajorSectionLine(line) {
  const t = line.trim();
  if (t.length > 120) return false;
  return MAJOR_SECTION_RE.test(t);
}

function classifyNonTableLine(line) {
  const { isHeading, text } = stripHeadingPrefix(line);
  if (isHeading) return { type: "heading", text };

  if (FOOTER_RE.test(line)) return { type: "footer", text: line.trim() };

  const bulletM = line.match(BULLET_RE);
  if (bulletM) return { type: "bullet", text: bulletM[1].trim() };

  const boldM = line.match(SUB_BOLD_RE);
  if (boldM) return { type: "subheading", text: boldM[1].trim() };

  if (SUB_LABEL_RE.test(line)) return { type: "subheading", text: line.trim() };

  if (isMajorSectionLine(line)) return { type: "heading", text: line.trim() };

  return { type: "line", text: line.trim() };
}

/** Remove stray ** from body text for display; strip model table-instruction prefixes from UI. */
export function stripInlineBoldMarkers(text) {
  if (!text || typeof text !== "string") return "";
  return text
    .replace(/\[Table\]\s*/gi, "")
    .replace(/TABLE_BRIEF:\s*/gi, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1");
}

/**
 * Split glued coverage lines (e.g. "Total SKUs: 1- Duty-Free…9Portfolio Overview")
 * so each metric and each major section title starts on its own line.
 */
export function formatDfSummarySectionBreaks(text) {
  if (!text || typeof text !== "string") return "";
  let t = text;
  t = t.replace(
    /\bTotal SKUs:\s*([0-9,]+)\s*[-–]\s*Duty[-\s]?Free Locations:/gi,
    "Total SKUs: $1\nDuty-Free Locations:"
  );
  t = t.replace(
    /\bDuty[-\s]?Free Locations:\s*([0-9,]+)\s*[-–]\s*Domestic Locations:/gi,
    "Duty-Free Locations: $1\nDomestic Locations:"
  );
  t = t.replace(/\bDomestic Locations:\s*([0-9,]+)\s*[-–]\s*/gi, "Domestic Locations: $1\n");
  t = t.replace(/(Domestic Locations:\s*[0-9,]+)(?=[A-Za-z])/gi, "$1\n\n");
  t = t.replace(/([0-9,]+)(Portfolio Overview)/gi, "$1\n\n$2");
  t = t.replace(
    /([0-9,]+)(Executive Pricing Brief|Price Position Summary|Executive Narrative|Category Intelligence|SKU-Level Highlights|AI Closing Note|Market Activity\b)/gi,
    "$1\n\n$2"
  );
  return t;
}

/** Remove attribution footer lines (model sometimes uses | or tabs before the date). */
export function stripDfSummaryReportFooter(text) {
  if (!text || typeof text !== "string") return "";
  let t = text.replace(/Pricepoint\.ai/gi, "Priceport.ai");
  const lines = t.split(/\r?\n/);
  const kept = lines.filter(
    (line) =>
      !/^Report by:/i.test(line.trim()) &&
      !/\bpromot(?:ion|ions|ional)?\b/i.test(line) &&
      !/\bprice changes observed across competitors\b/i.test(line) &&
      !/\bcompetitor price changes observed\b/i.test(line)
  );
  t = kept.join("\n");
  t = t.replace(/^\s*\d{4}-\d{2}-\d{2}\s*$/gm, "");
  t = t.replace(/\n{3,}/g, "\n\n");
  return formatDfSummarySectionBreaks(t).trim();
}

export function parseDfAiSummaryBlocks(text) {
  const lines = (text || "").split("\n");
  const blocks = [];
  let bulletBuffer = [];

  const flushBullets = () => {
    if (bulletBuffer.length > 0) {
      blocks.push({ type: "bullets", items: [...bulletBuffer] });
      bulletBuffer = [];
    }
  };

  let i = 0;
  let pendingTableBrief = null;

  const peekNextNonEmptyLine = (from) => {
    let j = from;
    while (j < lines.length) {
      const t = String(lines[j] ?? "").trim();
      if (t) return { index: j, line: t };
      j += 1;
    }
    return null;
  };

  while (i < lines.length) {
    const raw = lines[i] ?? "";
    const line = raw.trim();
    if (!line) {
      i += 1;
      continue;
    }

    if (line.includes("|")) {
      flushBullets();
      const tableRows = [];
      while (i < lines.length && String(lines[i] || "").includes("|")) {
        const row = String(lines[i])
          .split("|")
          .map((c) => c.trim())
          .filter((c) => c.length > 0);
        if (row.length > 0) tableRows.push(row);
        i += 1;
      }
      const normalizedRows = tableRows.filter(
        (row) => !(row.length > 0 && row.every((cell) => SEPARATOR_CELL.test(cell)))
      );
      if (normalizedRows.length > 0) {
        const brief = pendingTableBrief || undefined;
        pendingTableBrief = null;
        blocks.push({ type: "table", rows: normalizedRows, brief });
      } else {
        pendingTableBrief = null;
      }
      continue;
    }

    const capMatch = line.match(TABLE_CAPTION_RE) || line.match(TABLE_BRIEF_RE);
    if (capMatch) {
      flushBullets();
      const next = peekNextNonEmptyLine(i + 1);
      if (next && next.line.includes("|")) {
        pendingTableBrief = capMatch[1].trim();
        i += 1;
        continue;
      }
      blocks.push({ type: "line", text: capMatch[1].trim() });
      i += 1;
      continue;
    }

    const classified = classifyNonTableLine(line);
    if (classified.type === "bullet") {
      bulletBuffer.push(classified.text);
      i += 1;
      continue;
    }

    flushBullets();

    blocks.push(classified);
    i += 1;
  }

  flushBullets();
  return blocks;
}
